/**
 * Light mode Connect AI palette — teal/slate primary on soft mint surfaces.
 */
const colors = {
  background: {
    /** Soft mint canvas aligned with Connect AI marketing */
    default: "#F4F8F7",
    paper: "#FFFFFF",
    card: "#FBFCFB",
    sidenav: "#E6EEED",
    cardBorder: "#D8E0E8",
    cardBorderLeft: "#3D7A76",
    section: "#FFFFFF",
    sectionAlt: "#EEF4F3",
    /** Soft tonal fill for secondary / icon actions */
    tonal: "rgba(48, 65, 83, 0.04)",
    tonalHover: "rgba(48, 65, 83, 0.07)",
  },

  text: {
    primary: "#304153",
    secondary: "#5C6F7A",
    disabled: "rgba(48, 65, 83, 0.38)",
    main: "#304153",
    focus: "#24313E",
  },

  divider: "#D8E0E8",

  action: {
    active: "rgba(48, 65, 83, 0.56)",
    hover: "rgba(61, 122, 118, 0.06)",
    selected: "rgba(61, 122, 118, 0.08)",
    disabled: "rgba(48, 65, 83, 0.26)",
    disabledBackground: "rgba(48, 65, 83, 0.12)",
    focus: "rgba(61, 122, 118, 0.14)",
  },

  transparent: {
    main: "rgba(0, 0, 0, 0)",
  },

  white: {
    main: "#FFFFFF",
    focus: "#FFFFFF",
  },

  black: {
    light: "#424242",
    main: "#242424",
    focus: "#1A1A1A",
  },

  primary: {
    main: "#3D7A76",
    light: "#83BAB7",
    dark: "#2D5F5C",
    focus: "#4F8F8B",
    contrastText: "#FFFFFF",
  },

  secondary: {
    main: "#5C6F7A",
    light: "#8DA060",
    dark: "#304153",
    focus: "#304153",
    contrastText: "#FFFFFF",
  },

  info: {
    main: "#4F8F8B",
    light: "#83BAB7",
    dark: "#3D7A76",
    focus: "#3D7A76",
    contrastText: "#FFFFFF",
  },

  success: {
    main: "#6B8248",
    light: "#8DA060",
    dark: "#556838",
    focus: "#556838",
    contrastText: "#FFFFFF",
  },

  warning: {
    main: "#B45309",
    light: "#F59E0B",
    dark: "#92400E",
    focus: "#92400E",
    contrastText: "#FFFFFF",
  },

  error: {
    main: "#B91C1C",
    light: "#EF4444",
    dark: "#991B1B",
    focus: "#991B1B",
    contrastText: "#FFFFFF",
  },

  light: {
    main: "#F5F5F5",
    focus: "#E5E5E5",
  },

  dark: {
    main: "#242424",
    focus: "#1A1A1A",
  },

  grey: {
    50: "#FAFAFA",
    100: "#F5F5F5",
    200: "#E5E5E5",
    300: "#D1D1D1",
    400: "#ADADAD",
    500: "#616161",
    600: "#525252",
    700: "#424242",
    800: "#242424",
    900: "#1A1A1A",
  },

  priority: {
    critical: { main: "#B91C1C", focus: "#991B1B" },
    high: { main: "#B45309", focus: "#92400E" },
    medium: { main: "#0369A1", focus: "#075985" },
    low: { main: "#047857", focus: "#065F46" },
  },

  status: {
    new: { main: "#0369A1", focus: "#075985" },
    assigned: { main: "#B45309", focus: "#92400E" },
    inProgress: { main: "#6D28D9", focus: "#5B21B6" },
    onHold: { main: "#525252", focus: "#424242" },
    resolved: { main: "#047857", focus: "#065F46" },
    closed: { main: "#57534E", focus: "#44403C" },
    cancelled: { main: "#B91C1C", focus: "#991B1B" },
  },

  gradients: {
    primary: { main: "#3D7A76", state: "#2D5F5C" },
    secondary: { main: "#5C6F7A", state: "#304153" },
    info: { main: "#4F8F8B", state: "#3D7A76" },
    success: { main: "#6B8248", state: "#556838" },
    warning: { main: "#B45309", state: "#92400E" },
    error: { main: "#B91C1C", state: "#991B1B" },
    light: { main: "#F4F8F7", state: "#E6EEED" },
    dark: { main: "#304153", state: "#24313E" },
  },

  socialMediaColors: {
    facebook: { main: "#1877F2", dark: "#166FE5" },
    twitter: { main: "#1DA1F2", dark: "#1A91DA" },
    instagram: { main: "#E4405F", dark: "#D93A57" },
    linkedin: { main: "#0077B5", dark: "#005885" },
    pinterest: { main: "#BD081C", dark: "#8C0615" },
    youtube: { main: "#FF0000", dark: "#CC0000" },
    vimeo: { main: "#1AB7EA", dark: "#13A3D2" },
    slack: { main: "#4A154B", dark: "#350D36" },
    dribbble: { main: "#EA4C89", dark: "#E73177" },
    github: { main: "#24292E", dark: "#171A1D" },
    reddit: { main: "#FF4500", dark: "#E03D00" },
    tumblr: { main: "#35465C", dark: "#2A3749" },
  },

  badgeColors: {
    primary: { background: "#E8F3F2", text: "#2D5F5C" },
    secondary: { background: "#F4F8F7", text: "#304153" },
    info: { background: "#E4F0EF", text: "#3D7A76" },
    success: { background: "#EEF3E6", text: "#556838" },
    warning: { background: "#FEF3C7", text: "#92400E" },
    error: { background: "#FEE2E2", text: "#991B1B" },
    light: { background: "#FFFFFF", text: "#5C6F7A" },
    dark: { background: "#F4F8F7", text: "#304153" },
  },

  coloredShadows: {
    primary: "#3D7A76",
    secondary: "#5C6F7A",
    info: "#4F8F8B",
    success: "#6B8248",
    warning: "#B45309",
    error: "#B91C1C",
    light: "#83BAB7",
    dark: "#304153",
  },

  inputBorderColor: "#C5D0D6",

  tabs: {
    indicator: { boxShadow: "#E5E5E5" },
  },

  reportChat: {
    appBg: "#F4F8F7",
    railBg: "#E6EEED",
    railBorder: "#D8E0E8",
    surface: "#FFFFFF",
    composerBg: "#FFFFFF",
    userBubble: "#E8F3F2",
    userText: "#304153",
    assistantText: "#304153",
    muted: "#5C6F7A",
    border: "#D8E0E8",
    selectedList: "rgba(61, 122, 118, 0.12)",
    accentGrad: "linear-gradient(135deg, #3D7A76 0%, #83BAB7 100%)",
    sendIcon: "#FFFFFF",
    thinking: "#5C6F7A",
    sparkle: "#3D7A76",
    errorText: "#B91C1C",
    listeningBg: "#B91C1C",
    listeningHoverBg: "#991B1B",
    overlayLight: "rgba(48, 65, 83, 0.04)",
    overlayDark: "rgba(255, 255, 255, 0.08)",
    surfaceOverlayLight: "rgba(48, 65, 83, 0.04)",
    surfaceOverlayDark: "rgba(255, 255, 255, 0.06)",
    elevatedSurfaceLight: "rgba(48, 65, 83, 0.02)",
    elevatedSurfaceDark: "rgba(255, 255, 255, 0.04)",
    chipSelectedLight: "rgba(61, 122, 118, 0.12)",
    chipSelectedDark: "rgba(131, 186, 183, 0.16)",
    composerSurfaceLight: "rgba(255, 255, 255, 0.96)",
    composerSurfaceDark: "rgba(36, 48, 57, 0.96)",
    shadowLight: "0 1px 4px rgba(48, 65, 83, 0.08)",
    shadowDark: "0 2px 8px rgba(0, 0, 0, 0.35)",
    shadowComposerLight: "0 2px 12px rgba(48, 65, 83, 0.08)",
    shadowComposerDark: "0 2px 8px rgba(0, 0, 0, 0.35)",
    insetBorderLight: "inset 0 0 0 1px rgba(48, 65, 83, 0.06)",
    insetBorderDark: "inset 0 0 0 1px rgba(255, 255, 255, 0.06)",
    hoverMutedLight: "rgba(48, 65, 83, 0.06)",
    hoverMutedDark: "rgba(255, 255, 255, 0.08)",
    printBorder: "#D8E0E8",
    printHeaderBg: "#F4F8F7",
    printText: "#304153",
  },
};

export default colors;
